import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';

import type { Credential } from '@/types';
import { logger } from '@/utils/logger';

import { useSaveCredentialsMutation } from '../api/environmentsApi';

interface ManageEnvironmentVariablesProps {
  envName: string;
  envId: string;
  envVarsCred: Credential | undefined;
  onClose: () => void;
}

interface EnvVariable {
  id: number;
  name: string;
  value: string | number;
  sensitive?: boolean;
  datatype?: string;
}

const dataTypes = ['String', 'Number'];

/**
 * Management interface for environment variables (in modal)
 * Matches production dataProtect manageEnvVars.js
 */
export const ManageEnvironmentVariables = ({
  envId,
  envVarsCred,
  onClose,
}: ManageEnvironmentVariablesProps) => {
  const { appId, tenantId } = useApp();
  const [envVars, setEnvVars] = useState<EnvVariable[]>([]);
  const [saveCredentials, { isLoading: isSaving }] = useSaveCredentialsMutation();

  useEffect(() => {
    if (envVarsCred?.credentialsJson?.envVars) {
      setEnvVars(envVarsCred.credentialsJson.envVars as EnvVariable[]);
    }
  }, [envVarsCred]);

  const handleNameChange = (id: number, newName: string) => {
    setEnvVars((vars) => vars.map((v) => (v.id === id ? { ...v, name: newName } : v)));
  };

  const handleValueChange = (id: number, newValue: string) => {
    setEnvVars((vars) =>
      vars.map((v) => {
        if (v.id === id) {
          let value: string | number = newValue;
          if (v.datatype === 'Number') {
            value = parseInt(newValue, 10) || 0;
          }
          return { ...v, value };
        }
        return v;
      })
    );
  };

  const handleDatatypeChange = (id: number, newDatatype: string) => {
    setEnvVars((vars) =>
      vars.map((v) => {
        if (v.id === id) {
          let { value } = v;
          if (newDatatype === 'String') {
            value = value.toString();
          } else if (newDatatype === 'Number') {
            value = parseInt(value.toString(), 10) || 0;
          }
          return { ...v, datatype: newDatatype, value };
        }
        return v;
      })
    );
  };

  const handleSensitiveToggle = (id: number) => {
    setEnvVars((vars) => vars.map((v) => (v.id === id ? { ...v, sensitive: !v.sensitive } : v)));
  };

  const handleBlankStringToggle = (id: number) => {
    setEnvVars((vars) =>
      vars.map((v) => {
        if (v.id === id) {
          if (v.datatype === 'Empty String') {
            return { ...v, datatype: '' };
          }
          return { ...v, datatype: 'Empty String', value: '', sensitive: false };
        }
        return v;
      })
    );
  };

  const handleDelete = (id: number) => {
    setEnvVars((vars) => vars.filter((v) => v.id !== id));
  };

  const addNewSensitiveVar = () => {
    const maxId = envVars.length > 0 ? Math.max(...envVars.map((v) => v.id)) : 0;
    const newVar: EnvVariable = {
      id: maxId + 1,
      name: '',
      value: '',
      sensitive: true,
      datatype: 'String',
    };
    setEnvVars([...envVars, newVar]);
  };

  const addNewDefaultVar = () => {
    const maxId = envVars.length > 0 ? Math.max(...envVars.map((v) => v.id)) : 0;
    const newVar: EnvVariable = {
      id: maxId + 1,
      name: '',
      value: '',
      sensitive: false,
      datatype: 'String',
    };
    setEnvVars([...envVars, newVar]);
  };

  const saveEnvVars = async () => {
    try {
      await saveCredentials({
        tenantId,
        appId,
        envId,
        key: 'environmentvariables',
        data: {
          key: 'environmentvariables',
          providerLabel: 'EnvironmentVariables',
          credentialType: 'EnvironmentVariables',
          credentialsJson: {
            connectionName: 'EnvironmentVariables',
            ApiAccessible: false,
            defaultConnection: false,
            envVars,
          },
        },
      }).unwrap();
      onClose();
    } catch (err) {
      logger.error('Failed to save environment variables:', err);
    }
  };

  const isDisabled = envVars.some((v) => !v.name || v.name === '');

  // Sort alphabetically by name for display
  const sortedEnvVars = [...envVars].sort((a, b) =>
    (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
  );

  return (
    <Box sx={{ height: 500 }}>
      <Box
        sx={{
          backgroundColor: '#EEE',
          marginRight: 2.5,
          marginLeft: 3.75,
          minHeight: '400px',
        }}
      >
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid #ddd' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%', fontWeight: 'bold', fontSize: '13px' }}>
                  Name
                </TableCell>
                <TableCell sx={{ width: '25%', fontWeight: 'bold', fontSize: '13px' }}>
                  Value
                </TableCell>
                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontSize: '13px' }}>
                  Data Types
                </TableCell>
                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontSize: '13px' }}>
                  Sensitive Value
                </TableCell>
                <TableCell sx={{ width: '15%', fontWeight: 'bold', fontSize: '13px' }}>
                  Blank String
                </TableCell>
                <TableCell sx={{ width: '10%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEnvVars.map((v) => {
                const isBlank = v.datatype === 'Empty String';
                return (
                  <TableRow key={v.id}>
                    <TableCell>
                      <TextField
                        value={v.name}
                        onChange={(e) => handleNameChange(v.id, e.target.value)}
                        placeholder="key"
                        fullWidth
                        size="small"
                        required
                        sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={v.value}
                        onChange={(e) => handleValueChange(v.id, e.target.value)}
                        placeholder="value"
                        fullWidth
                        size="small"
                        type={(() => {
                          if (v.datatype === 'Number') return 'number';
                          if (v.sensitive) return 'password';
                          return 'text';
                        })()}
                        disabled={isBlank}
                        sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={v.datatype || 'String'}
                        onChange={(e) => handleDatatypeChange(v.id, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ fontSize: '13px' }}
                      >
                        {dataTypes.map((dt) => (
                          <MenuItem key={dt} value={dt} sx={{ fontSize: '13px' }}>
                            {dt}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Checkbox
                        checked={v.sensitive ?? false}
                        onChange={() => handleSensitiveToggle(v.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Checkbox
                        checked={isBlank}
                        onChange={() => handleBlankStringToggle(v.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <DeleteIcon
                        onClick={() => handleDelete(v.id)}
                        sx={{
                          width: 28,
                          height: 28,
                          cursor: 'pointer',
                          color: 'error.main',
                          '&:hover': { color: 'error.dark' },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ marginTop: 1.25, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addNewSensitiveVar}
          sx={{
            width: 'auto',
            minWidth: 210,
            fontWeight: 'normal',
            height: 36,
            fontSize: '13px',
            textTransform: 'none',
            backgroundColor: '#4a90e2',
            color: '#fff',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: '#357abd',
            },
          }}
        >
          Add a sensitive variable
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addNewDefaultVar}
          sx={{
            width: 'auto',
            minWidth: 150,
            fontWeight: 'normal',
            height: 36,
            fontSize: '13px',
            textTransform: 'none',
            backgroundColor: '#4a90e2',
            color: '#fff',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: '#357abd',
            },
          }}
        >
          Add a variable
        </Button>
      </Box>

      <Box
        sx={{
          marginTop: 2.5,
          marginBottom: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          px: 3,
          pb: 1,
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
          sx={{
            width: 100,
            fontWeight: 'normal',
            height: 34,
            fontSize: '13px',
            textTransform: 'none',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveEnvVars}
          disabled={isDisabled || isSaving}
          sx={{
            width: 100,
            fontWeight: 'normal',
            height: 34,
            fontSize: '13px',
            textTransform: 'none',
            backgroundColor: '#4a90e2',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#357abd',
            },
            '&:disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
            },
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};
